import * as React from "react";
import { useNavigate } from "react-router-dom";
import { Grid, Stack, useMediaQuery } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { Account, YesNo } from "@/types";
import { Attachment } from "@/types/Attachment";
import { Case } from "@/types/Case";
import urlPaths from "../../urlPaths";
import { Header } from "@/components/atoms/Header";
import { SimpleTemplate } from "@/templates/SimpleTemplate";
import { Accounts } from "./Accounts";
import { getCase } from "@/services/api/caseApi";
import { CallIcon } from "@/components/atoms/HelpInfo";
import { AdditionalServicesAccordion } from "./AdditionalServices";
import { CaseSummary } from "./CaseSummary";
import { KeyDocuments } from "./KeyDocuments";
import { DashboardInfoContext } from "@/store/DashboardProvider";
import { useBrandingContext } from "@/contexts/BrandingContext";
import { InheritNowAccordion } from "./InheritNowAccordion";
import { AdditionalServicesAccount } from "./AdditionalServicesAccount";
import { IntroductoryText } from "@/components/molecules/IntroductoryText";
import { noAccessToDashboardYet } from "@/helpers/dashboard-helpers";
import { useBackendContext } from "@/store/BackendStatusProvider";

type StatusPageProps = {
  readonly caseId: string;
  readonly signature: string | null;
};

export const StatusPage: React.FC<StatusPageProps> = ({ caseId, signature }) => {
  const {
    state: { refreshCounter },
    dispatch,
  } = React.useContext(DashboardInfoContext);
  const navigate = useNavigate();

  const [state, setState] = React.useState({ loading: false, error: false, caseRecord: null } as {
    loading: boolean;
    error: boolean;
    caseRecord: (Case & { accounts: Array<Account>; attachments?: Attachment[] }) | null;
  });

  const { caseRecord } = state;
  React.useEffect(() => {
    getCase({ caseId, signature })
      .then((res) => {
        if (!res.data?.case) return Promise.reject(new Error("Missing data."));
        const { case: caseRecord } = res.data;
        caseRecord.formData = res.data.notifierFormState;
        dispatch({
          type: "SET_ALL",
          payload: { ...res.data, caseId: caseId, signature: signature },
        });

        if (noAccessToDashboardYet(caseRecord)) {
          navigate(urlPaths.root(), { replace: true });
          return;
        }
        setState({ loading: false, error: false, caseRecord });
      })
      .catch((err) => {
        setState((s) => ({ ...s }));
        console.warn({ err });
      });
  }, [caseId, signature, navigate, refreshCounter]);

  if (!caseRecord) return null;

  return <StatusPageView />;
};

export const StatusPageView: React.FC = () => {
  const theme = useTheme();
  const matches = useMediaQuery(theme.breakpoints.down("md"));
  const { state } = useBackendContext();
  if (state?.featureFlags.inheritNowCrossSelling) return <StatusPageInheritNow />;
  if (matches) {
    // mobile
    return (
      <SimpleTemplate busy={false} headerType="signout">
        <Stack rowGap={2}>
          <Header level={1}>Accounts dashboard</Header>
          <IntroductoryText />
          <AdditionalServicesAccount />
          <AdditionalServicesAccordion />
          <Accounts />
          <div>
            <h2>Documents</h2>
            <KeyDocuments />
          </div>
        </Stack>
      </SimpleTemplate>
    );
  }
  // desktop
  return (
    <SimpleTemplate busy={false} headerType="signout">
      <Stack rowGap={2}>
        <Header level={1}>Accounts dashboard</Header>
        <Grid container spacing={4}>
          <Grid item xs={7}>
            <IntroductoryText />
          </Grid>
          <Grid item xs={5}>
            <CaseSummary />
            <KeyDocuments />
          </Grid>
        </Grid>
        <AdditionalServicesAccordion />
        <AdditionalServicesAccount />
        <Accounts />
      </Stack>
    </SimpleTemplate>
  );
};

const StatusPageInheritNow: React.FC = () => {
  const theme = useTheme();
  const matches = useMediaQuery(theme.breakpoints.down("md"));

  if (matches) {
    // mobile
    return (
      <SimpleTemplate busy={false} headerType="signout">
        <Stack rowGap={2}>
          <Header level={1}>Accounts dashboard</Header>
          <Accounts />

          <InheritNowAccordion />
          <IntroductoryText />
          <div>
            <h2>Documents</h2>
            <KeyDocuments />
          </div>
          <AdditionalServicesAccount />
          <AdditionalServicesAccordion />
        </Stack>
      </SimpleTemplate>
    );
  }
  // desktop
  return (
    <SimpleTemplate busy={false} headerType="signout">
      <Stack rowGap={2}>
        <Header level={1}>Accounts dashboard</Header>
        <Accounts />
        <br />
        <InheritNowAccordion />
        <Grid container spacing={4}>
          <Grid item xs={7}>
            <IntroductoryText />
          </Grid>
          <Grid item xs={5}>
            <CaseSummary />
            <KeyDocuments />
          </Grid>
        </Grid>
        <AdditionalServicesAccount />
        <AdditionalServicesAccordion />
      </Stack>
    </SimpleTemplate>
  );
};
